import {
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { FormControl, Validators, AbstractControl } from '@angular/forms';
import { delay, filter, takeUntil } from 'rxjs/operators';
import { MatTabGroup } from '@angular/material/tabs';
import { environment } from 'src/environments/environment';
import { sweetAlertService } from '../../shared/sweetalert/sweetalert.service';
import { DomSanitizer } from '@angular/platform-browser';

import { AuthStoreService } from '../../core/auth/auth.service';
import { ExpenseCategoryService } from '../../core/backend/expense-category/expense-category.service';
import { JobService } from '../../core/backend/job/job.service';
import { JobStoreService } from '../../core/job/job.service';
import { UserBackendModel } from '../../core/backend/customer/model/UserBackend.model';
import { SettingStoreService } from '../../core/setting/setting.service';
import { AlertMessageCommonService } from '../../shared/alert-message-common/alert-message-common.service';

import { EquipmentType } from '../../core/backend/shared/model/equipment';
import { ExpenseCategoryModel } from '../../core/backend/expense-category/model/expense-category.model';

import { JobModel } from '../../core/backend/job/model/job.model';
import { UserModel } from '../../core/backend/user/model/user.model';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { Router } from '@angular/router';
import { ExpenseService } from '../../core/backend/expenses/expense.service';
import { ExpenseModel } from '../../core/backend/expenses/model/expense.model';
import { BoolAsInt } from '../../core/backend/shared/model/bool-as-int';
import { SpinnerService } from '../../shared/spinner/spinner.service';
import {
  MatAutocomplete,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { invalid } from 'moment';

@Component({
  selector: 'app-receipt-capture-detail',
  templateUrl: './receipt-capture-detail.component.html',
  styleUrls: ['./receipt-capture-detail.component.scss'],
})
export class ReceiptCaptureDetailComponent implements OnInit, OnDestroy {
  @ViewChild(MatTabGroup) tabGroup: MatTabGroup;
  @ViewChild('jobInput', { read: MatAutocompleteTrigger })
  jobInput!: MatAutocompleteTrigger;
  @ViewChild('equipmentInput', { read: MatAutocompleteTrigger })
  equipmentInput!: MatAutocompleteTrigger;
  @ViewChild('employeeInput', { read: MatAutocompleteTrigger })
  employeeInput!: MatAutocompleteTrigger;
  @ViewChild('scrollContainer') scrollContainer!: ElementRef; // Reference to the scrolling container

  ngUnsubscribe$: Subject<any> = new Subject<any>();

  previewedFile: string = '';
  previewedFileName: string = '';
  images: File[] = [];
  isLoading: boolean = false;
  isSubmitting: boolean = false;
  submitAttempted: boolean = false;
  filesUploaded: number = 0;
  filesUploading: number = 0;
  progressBar: HTMLProgressElement | null = null;
  dropArea: HTMLElement | null;
  selectedIndex: number = 0;
  canShowFuelFields: boolean = false;
  canShowMaintFields: boolean = false;
  canShowLodgingFields: boolean = false;
  viewingUserInfo: UserBackendModel;

  equipments: EquipmentType[] = [];
  favoriteEquipments: EquipmentType[] = [];
  favoriteJobs: JobModel[] = [];
  jobs$: Observable<JobModel[]>;
  jobs: JobModel[] = [];
  users$: Observable<UserModel[]>;
  users: UserModel[];
  currentUser$: Observable<UserBackendModel>;
  expenseCategories: ExpenseCategoryModel[] = [];
  filteredEquipments: EquipmentType[] = [];
  filteredFavoriteEquipments: EquipmentType[] = [];
  filteredFavoriteJobs: JobModel[] = [];
  filteredJobs: JobModel[] = [];
  filteredUsers: UserModel[] = [];
  canModifyEmployee: Boolean = false;

  // Form Fields
  amount = new FormControl('', [Validators.required]);
  employeeControl = new FormControl('', [Validators.required]);
  jobControl = new FormControl('');
  selectedDate = new FormControl(new Date());
  equipmentControl = new FormControl('');
  expenseControl = new FormControl('', Validators.required);
  mileage = new FormControl(null);
  gallons = new FormControl(null);
  nights = new FormControl(null);
  notes = new FormControl('');

  // Mirrors the DB column types: gallons is DECIMAL(8,3), nights is SMALLINT.
  maxGallons = 99999.999;
  maxNights = 32767;

  notesCharCount = 0;
  maxCharLimit = 750;
  isAmountNegative = false;

  deviceType = 'Desktop';

  // Add / Edit mode
  mode: 'add' | 'edit' = 'add';
  id: number = 0;

  favoriteLabel = 'Favorite';
  public loading$ = new BehaviorSubject<boolean>(false);

  equipmentControlFocused: boolean = false;
  jobControlFocused: boolean = false;
  employeeControlFocused: boolean = false;

  constructor(
    private authStoreService: AuthStoreService,
    private jobService: JobService,
    private jobStoreService: JobStoreService,
    private expenseCategoryService: ExpenseCategoryService,
    private sweetAlertService: sweetAlertService,
    private route: Router,
    private expenseService: ExpenseService,
    private sanitizer: DomSanitizer,
    private spinnerService: SpinnerService,
    private settingStoreService: SettingStoreService,
    private alertMessageCommonService: AlertMessageCommonService
  ) {}

  ngOnInit(): void {
    this.deviceType = this.detectMobileDevice();
    console.log(this.deviceType);
    this.initDragDropListener();
    this.currentUser$ = this.authStoreService.getCurrentUserInfo();
    this.currentUser$
      .pipe(
        filter((data) => !!data),
        delay(500),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe((fetchedData) => {
        this.viewingUserInfo = fetchedData;
        const userData = this.viewingUserInfo;
        const userPermissions = JSON.parse(userData.password);
        this.canModifyEmployee = false;
        for (let i = 0; i < Object.keys(userPermissions).length; i++) {
          let id = userPermissions[i].permission_id;
          let edit = userPermissions[i].permission_edit_option;
          let view = userPermissions[i].permission_view_option;

          switch (id) {
            case 20:
              if (view.includes('All')) {
                this.canModifyEmployee = true;
              }
          }
        }
        if (this.mode === 'edit') {
          this.getExpenseData(qp.id);
        } else {
          const selectedEmployee = this.users.find(
            (el) => el.id === this.viewingUserInfo.id
          );

          this.employeeControl.setValue(this.getUserLabel(selectedEmployee));
        }
      });

    this.progressBar = document.getElementById(
      'progress-bar'
    ) as HTMLProgressElement;

    const qp = this.route.parseUrl(this.route.url).queryParams;

    this.mode = qp.mode;
    this.id = qp.id;

    this.jobs$ = this.jobStoreService.getAllJobs();
    this.jobStoreService.requestJobs(this.getPayloadForJobs());

    this.jobs$.subscribe((fetchedJobs) => {
      this.populateJobDropdown(fetchedJobs);
    });

    this.users$ = this.settingStoreService.getAllUsers();
    this.settingStoreService.requestUsers(true);
    this.users$.subscribe((fetchedUsers) => {
      this.users = fetchedUsers;
      this.filteredUsers = this.users;
    });

    this.populateExpenseCategoryDropdown();
    this.getAllEquipment();
    this.spinnerService.manageSpinnerState();

    this.jobControl.valueChanges.subscribe((value) =>
      this._filter(value, 'jobs')
    );
    this.equipmentControl.valueChanges.subscribe((value) =>
      this._filter(value, 'equipments')
    );
    this.employeeControl.valueChanges.subscribe((value) =>
      this._filter(value, 'employees')
    );
  }

  handleJobFocus() {
    this.jobControlFocused = true;
    this.jobInput.position = 'below';
    this.jobInput.updatePosition();
    this.jobInput.openPanel();
    this.addRemoveBackgroundScroll('add');
  }

  handleJobBlur() {
    this.jobControlFocused = false;
    this.addRemoveBackgroundScroll();
    this.addRemoveInvalidError(this.jobControl, false);

    if (this.jobControl.value && this.jobControl.value != '') {
      const existsInFavoriteJobs = this.favoriteJobs.find(
        (el) => this.getJobLabel(el) === this.jobControl.value
      );
      const existsInJobs = this.jobs.find(
        (el) => this.getJobLabel(el) === this.jobControl.value
      );

      if (!existsInFavoriteJobs && !existsInJobs) {
        this.addRemoveInvalidError(this.jobControl, true);
      }
    }
  }

  handleEquipmentFocus() {
    this.equipmentControlFocused = true;
    this.equipmentInput.position = 'below';
    this.equipmentInput.updatePosition();
    this.equipmentInput.openPanel();
    this.addRemoveBackgroundScroll('add');
  }

  handleEquipmentBlur() {
    this.equipmentControlFocused = false;
    this.addRemoveBackgroundScroll();
    this.addRemoveInvalidError(this.equipmentControl, false);

    if (this.equipmentControl.value && this.equipmentControl.value != '') {
      const selectedEquipment = this.equipments.find(
        (el) => this.getEquipmentLabel(el) === this.equipmentControl.value
      );

      if (!selectedEquipment) {
        this.addRemoveInvalidError(this.equipmentControl, true);
      }
    }
  }

  handleUserFocus() {
    this.employeeControlFocused = true;
    this.employeeInput.position = 'below';
    console.log(this.employeeInput.position);
    this.employeeInput.updatePosition();
    this.employeeInput.openPanel();
    this.addRemoveBackgroundScroll('add');
  }

  handleUserBlur() {
    this.employeeControlFocused = false;
    this.addRemoveBackgroundScroll();
    this.addRemoveInvalidError(this.employeeControl, false);

    if (this.employeeControl.value && this.employeeControl.value != '') {
      const existsInUsers = this.users.find(
        (el) => this.getUserLabel(el) === this.employeeControl.value
      );

      if (!existsInUsers) {
        this.addRemoveInvalidError(this.employeeControl, true);
      }
    }
  }

  addRemoveInvalidError(control: AbstractControl, isAdd: Boolean) {
    if (isAdd) {
      control.setErrors({ invalidSelection: true });
    } else {
      control.setErrors(null);
      control.updateValueAndValidity();
    }
  }
  scrollToTop(element: HTMLElement, inputField: String) {
    if (this.deviceType === 'iOS') {
      const rect = element.getBoundingClientRect();
      const scrollPosition = rect.top + window.scrollY - 30;
      setTimeout(() => {
        window.scrollTo({
          top: scrollPosition,
          behavior: 'smooth',
        });
      }, 200);
    }
  }
  addRemoveBackgroundScroll(action = 'remove') {
    if (this.deviceType === 'Android') {
      console.log('android');
      if (action === 'add') {
        this.scrollContainer.nativeElement.classList.add('no-scroll');
      } else {
        this.scrollContainer.nativeElement.classList.remove('no-scroll');
      }
    }
  }

  getToken(): string {
    const tokenFromStorage = sessionStorage.getItem('LOGIN_TOKEN_KEY') ?? '';
    return JSON.parse(tokenFromStorage);
  }

  async getExpenseData(expenseId: number) {
    const res = await this.expenseService.fetchExpenseById(expenseId);
    this.setForm(res);
  }

  async setForm(res: ExpenseModel) {
    this.amount.setValue(
      Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Number(res.amount))
    );
    this.tabGroup.selectedIndex = res.receipt_type - 1;

    const selectedEmployee = this.users.find((el) => el.id === res.employee_id);

    this.employeeControl.setValue(this.getUserLabel(selectedEmployee));

    const selectedJob = this.jobs.find((el) => el.id === res.job_id);

    if (selectedJob) {
      this.jobControl.setValue(this.getJobLabel(selectedJob));
    }

    this.expenseControl.setValue(res.expense_category_name);
    this.equipmentControl.setValue(res.equipment_num_name);
    const dateEntered = new Date(res.receipt_date + 'T12:00:00.000Z');
    this.selectedDate.setValue(dateEntered);
    this.mileage.setValue(
      Intl.NumberFormat('en-US').format(Number(res.mileage))
    );
    // Only hydrate when the API actually returned a value; a plain
    // Number(null) would render a misleading "0" in an empty field.
    if (res.gallons !== null && res.gallons !== undefined) {
      this.gallons.setValue(`${Number(res.gallons)}`);
    }
    if (res.nights !== null && res.nights !== undefined) {
      this.nights.setValue(`${Number(res.nights)}`);
    }
    if (res.notes) {
      this.notes.setValue(res.notes);
      this.notesCharCount = res.notes.length;
    }

    this.previewedFileName = res.s3_file_name;

    const mime = `image/${res.mime_type}`;

    const url = this.sanitizer.bypassSecurityTrustResourceUrl(
      `data:${mime};base64,${res.s3_file_base64}`
    );

    // @ts-ignore
    const byteCharacters = atob(res.s3_file_base64);
    const byteArrays = [];

    for (let i = 0; i < byteCharacters.length; i++) {
      byteArrays.push(byteCharacters.charCodeAt(i));
    }

    const byteArray = new Uint8Array(byteArrays);

    this.images = [new File([byteArray], res.s3_file_name)];

    // @ts-ignore
    this.previewedFile = url.changingThisBreaksApplicationSecurity as string;
  }

  detectMobileDevice(): string {
    const isTouchDevice =
      'ontouchstart' in window || navigator.maxTouchPoints > 0;

    if (isTouchDevice) {
      const userAgent = navigator.userAgent.toLowerCase();
      // Check if the user is on iPhone or Android using feature detection
      if (/iphone|ipod|ipad/.test(userAgent)) {
        return 'iOS';
      } else if (/android/.test(userAgent)) {
        return 'Android';
      }

      return 'Unknown';
    }

    return 'Desktop'; // Return Desktop if not a touch device
  }

  initDragDropListener() {
    this.dropArea = document.getElementById('drop-area');

    // Listeners to ignore default browser behavior
    this.dropArea?.addEventListener(
      'dragenter',
      (e) => this.handleDragEvent(e),
      false
    );
    this.dropArea?.addEventListener(
      'dragleave',
      (e) => this.handleDragEvent(e),
      false
    );
    this.dropArea?.addEventListener(
      'dragover',
      (e) => this.handleDragEvent(e),
      false
    );
    this.dropArea?.addEventListener(
      'drop',
      (e) => this.handleDragEvent(e),
      false
    );

    // Listeners to handle highlighting drop area
    this.dropArea?.addEventListener(
      'dragenter',
      (e) => this.handleHighlight(e),
      false
    );
    this.dropArea?.addEventListener(
      'dragover',
      (e) => this.handleHighlight(e),
      false
    );

    // Listeners to handle un-highlighting drop area
    this.dropArea?.addEventListener(
      'dragleave',
      (e) => this.handleUnHighLight(e),
      false
    );
    this.dropArea?.addEventListener(
      'drop',
      (e) => this.handleUnHighLight(e),
      false
    );

    // Listener for when a file has been dropped onto the upload element.
    this.dropArea?.addEventListener('drop', (e) => this.handleDrop(e), false);

    this.expenseControl.valueChanges.subscribe((res) =>
      this.handleExpenseChange(res)
    );
  }

  getPayloadForJobs() {
    const reqObj = {
      customer_id: null,
      department_id: null,
      state: null,
      city: null,
      search: null,
      status: null,
    };

    return reqObj;
  }

  async populateJobDropdown(jobs: JobModel[]) {
    this.favoriteJobs = jobs.filter((el) => el.is_job_favorite) ?? [];
    this.filteredFavoriteJobs = this.favoriteJobs;
    this.jobs = jobs;
    this.filteredJobs = jobs;
  }

  _filter(value: string, key: 'equipments' | 'jobs' | 'employees') {
    if (!key) return;

    switch (key) {
      case 'jobs':
        if (!value) {
          this.filteredFavoriteJobs = this.favoriteJobs;
          this.filteredJobs = this.jobs;
        } else {
          this.filteredFavoriteJobs =
            this.favoriteJobs.filter(
              (el) =>
                el.name.toLowerCase().includes(value.toLowerCase()) ||
                el.job_number.toLowerCase().includes(value.toLowerCase())
            ) ?? [];
          this.filteredJobs =
            this.jobs.filter(
              (el) =>
                el.name.toLowerCase().includes(value.toLowerCase()) ||
                el.job_number.toLowerCase().includes(value.toLowerCase())
            ) ?? [];
        }
        break;
      case 'equipments':
        if (!value) {
          this.filteredFavoriteEquipments = this.favoriteEquipments;
          this.filteredEquipments = this.equipments;
        } else {
          this.filteredFavoriteEquipments =
            this.favoriteEquipments.filter((el) =>
              el.full_equipment_name.toLowerCase().includes(value.toLowerCase())
            ) ?? [];
          this.filteredEquipments =
            this.equipments.filter((el) =>
              el.full_equipment_name.toLowerCase().includes(value.toLowerCase())
            ) ?? [];
        }
        break;
      case 'employees':
        if (!value) {
          this.filteredUsers = this.users;
        } else {
          this.filteredUsers =
            this.users.filter((el) =>
              this.getUserLabel(el).toLowerCase().includes(value.toLowerCase())
            ) ?? [];
        }
        break;
    }
  }

  onSelect(e) {
    this.filteredFavoriteEquipments = this.favoriteEquipments;
    this.filteredEquipments = this.equipments;
    this.filteredFavoriteJobs = this.favoriteJobs;
    this.filteredJobs = this.jobs;
    this.filteredUsers = this.users;

    if (e.option.value === '000 - Other') {
      this.notes.setValidators([Validators.required]);
    } else {
      this.notes.clearValidators();
    }

    this.notes.updateValueAndValidity();
  }

  async populateExpenseCategoryDropdown() {
    const categories = await this.expenseCategoryService.getAllCategories();
    this.expenseCategories = categories;
  }

  async getAllEquipment() {
    const equipments = await this.jobService.getAllEquipments(null);
    this.equipments = equipments;

    this.favoriteEquipments =
      equipments.filter((el) => el.is_equipment_favorite) ?? [];

    this.filteredFavoriteEquipments = this.favoriteEquipments;

    this.filteredEquipments = equipments;
  }

  handleHighlight(e: DragEvent) {
    this.dropArea?.classList.add('highlight');
  }

  handleUnHighLight(e: DragEvent) {
    this.dropArea?.classList.remove('highlight');
  }

  handleDragEvent(e: DragEvent) {
    e.preventDefault();
    e.stopPropagation();
  }

  handleChange(e: Event) {
    const target = e.target as HTMLInputElement;
    const files = target.files;

    this.handleFiles(files);
  }

  handleFiles(files: FileList) {
    if (!files?.length) return;

    this.images = Array.from([...files]);
    this.images.forEach((f) => this.previewFile(f));
  }

  handleDrop(e: DragEvent) {
    const dt = e.dataTransfer;
    const files = dt?.files;

    this.handleFiles(files);
  }

  handleExpenseChange(newValue: string) {
    if (!newValue) return;

    const isFuel = newValue.toLowerCase() === 'fuel';
    const isMaint = newValue.toLowerCase() === 'maintenance';
    const isLodging = newValue.toLowerCase() === 'lodging';

    this.canShowFuelFields = isFuel;
    this.canShowMaintFields = isMaint;
    this.canShowLodgingFields = isLodging;

    //update validations or reset
    if (isFuel) {
      this.mileage.setValidators(Validators.required);
      this.gallons.setValidators(Validators.required);
    } else {
      // clearValidators as well as reset: resetControls only nulls the current
      // errors, so a stale `required` would come back on the next
      // updateValueAndValidity() and leave a hidden control invalid.
      this.mileage.clearValidators();
      this.gallons.clearValidators();
      this.resetControls([this.mileage, this.gallons]);
    }
    if (isLodging) {
      this.nights.setValidators(Validators.required);
    } else {
      this.nights.clearValidators();
      this.resetControls([this.nights]);
    }
    if (isFuel || isMaint) {
      this.equipmentControl.setValidators(Validators.required);
    } else {
      this.resetControls([this.equipmentControl]);
    }

    this.mileage.updateValueAndValidity();
    this.gallons.updateValueAndValidity();
    this.nights.updateValueAndValidity();
    this.equipmentControl.updateValueAndValidity();
  }

  // Update the value as the user types
  onAmountChange(event: InputEvent) {
    const input = (event.target as HTMLInputElement).value;
    let initalValue = input.replace(/[^0-9]/g, '');
    //  initalValue.replace(/^0+(\.|$)/, '') || '';

    let formattedValue = '';

    switch (initalValue.length) {
      case 0:
        break;
      case 1:
        formattedValue = `0.0${initalValue}`;
        break;
      case 2:
        formattedValue = `0.${initalValue}`;
        break;
      default:
        let cents = initalValue.slice(-2);
        let dollar = initalValue.slice(0, -2);
        formattedValue = Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(Number(`${dollar}.${cents}`));
        break;
    }

    if (formattedValue == '0.00') {
      formattedValue = '';
    }

    if (event.data == '-') {
      //switch amount - or positive and handle if it is entered first
      this.isAmountNegative = !this.isAmountNegative;
    }

    if (this.isAmountNegative) {
      formattedValue = `-${formattedValue}`;
    }

    this.amount.setValue(formattedValue, { emitEvent: false }); // Prevents an infinite loop of value updates
  }

  // Update the value as the user types
  onMileageChange(event: InputEvent) {
    const input = (event.target as HTMLInputElement).value;
    let initalValue = input.replace(/[^0-9]/g, '');

    let formattedValue = Intl.NumberFormat('en-US').format(Number(initalValue));

    if (formattedValue == '0') {
      formattedValue = '';
    }
    this.mileage.setValue(formattedValue, { emitEvent: false }); // Prevents an infinite loop of value updates
  }

  // Gallons maps to DECIMAL(8,3): digits plus at most one decimal point and
  // three decimal places. Kept unformatted (no thousands separators) while
  // typing so a partially entered decimal like "12." survives.
  onGallonsChange(event: InputEvent) {
    const input = (event.target as HTMLInputElement).value;

    // Strip everything but digits and dots, then collapse to a single dot.
    let cleaned = input.replace(/[^0-9.]/g, '');
    const firstDot = cleaned.indexOf('.');
    if (firstDot !== -1) {
      cleaned =
        cleaned.slice(0, firstDot + 1) +
        cleaned.slice(firstDot + 1).replace(/\./g, '');
    }

    let [whole = '', decimals] = cleaned.split('.');
    // DECIMAL(8,3) => 5 digits before the point, 3 after.
    whole = whole.slice(0, 5);

    let formattedValue =
      decimals === undefined ? whole : `${whole}.${decimals.slice(0, 3)}`;

    // Blank a value that is entirely zero ("0", "000", "0.00") so an unset
    // field submits as NULL rather than 0. The DB column no longer carries a
    // DEFAULT 0 (task 43455564), and onSubmit omits falsy values entirely.
    // Mirrors the same rule in onNightsChange, but tested numerically so a
    // real sub-gallon measurement ("0.5") and a half-typed decimal ("0.")
    // both survive.
    if (
      formattedValue &&
      !formattedValue.endsWith('.') &&
      Number(formattedValue) === 0
    ) {
      formattedValue = '';
    }

    this.gallons.setValue(formattedValue, { emitEvent: false }); // Prevents an infinite loop of value updates
  }

  // Nights maps to SMALLINT: whole numbers only, capped at 32767.
  onNightsChange(event: InputEvent) {
    const input = (event.target as HTMLInputElement).value;
    let initalValue = input.replace(/[^0-9]/g, '');

    // Drop leading zeros so "007" reads back as "7".
    initalValue = initalValue.replace(/^0+(?=\d)/, '');

    if (initalValue && Number(initalValue) > this.maxNights) {
      initalValue = `${this.maxNights}`;
    }

    let formattedValue = initalValue;

    if (formattedValue == '0') {
      formattedValue = '';
    }
    this.nights.setValue(formattedValue, { emitEvent: false }); // Prevents an infinite loop of value updates
  }

  /* For previewing files in UI */
  async previewFile(file: File) {
    try {
      this.loading$.next(true);
      if (!file) return;

      if (file.type === 'application/pdf') {
        const image = await this.expenseService.convertPdfToPng(file);

        return this.readImage(image, true);
      }
      return this.readImage(file);
    } catch (error) {
      console.log(error);
      this.alertMessageCommonService.error(
        'error',
        'There was an error with the uploaded file. Please try again later.'
      );
    } finally {
      this.loading$.next(false);
    }
  }

  readImage(file: File, unsafe = false) {
    const reader = new FileReader();

    reader.readAsDataURL(file);

    reader.onloadend = async () => {
      if (unsafe) {
        const url = this.sanitizer.bypassSecurityTrustResourceUrl(
          reader.result as string
        );

        this.previewedFile = url as string;
      } else {
        this.previewedFile = reader.result as string;
      }
      this.previewedFileName = file.name;
    };
  }

  handleImageClick() {
    window.open(this.previewedFile);
  }

  removeImage(event) {
    this.images = [];
    this.previewedFile = '';
  }

  onTabClick(e: Record<string, number>) {
    this.selectedIndex = e.index;
  }

  getLabel() {
    return this.selectedIndex ? 'Amt Req' : 'Amt';
  }

  getJobLabel(job: JobModel) {
    return `${job.job_number} ${job.name}`;
  }

  getEquipmentLabel(unit: EquipmentType) {
    return `${unit.full_equipment_name}`;
  }

  getUserLabel(user: UserModel) {
    return `${user.lastname},  ${user.firstname}`;
  }

  getFavoriteLabel() {
    const unit = this.favoriteEquipments.find(
      (el) => this.getEquipmentLabel(el) === this.equipmentControl.value
    );

    this.favoriteLabel = unit ? 'Unfavorite' : 'Favorite';

    return this.favoriteLabel;
  }

  setFavoriteIconColor() {
    const selectedEquipment = this.equipments.find(
      (el) => this.getEquipmentLabel(el) === this.equipmentControl.value
    );

    const favClass = 'fa fa-star ylw_yellow';
    const unFavClass = 'fa fa-star gry';

    return selectedEquipment?.is_equipment_favorite ? favClass : unFavClass;
  }

  async onFavoritePress(event) {
    event.preventDefault();

    const selectedEquipment = this.equipments.find(
      (el) => this.getEquipmentLabel(el) === this.equipmentControl.value
    );

    if (!selectedEquipment) return;

    const isFav = selectedEquipment.is_equipment_favorite;
    const employee_id = this.viewingUserInfo.id;

    const payload = {
      equipment_id: selectedEquipment.id,
      employee_id,
      is_equipment_favorite: 1 as BoolAsInt, // BE handles switching this if record exists.
    };

    await this.jobService.favoriteSingleEquipment(payload);
    await this.getAllEquipment();

    this.favoriteLabel = isFav ? 'Favorite' : 'Unfavorite';
  }

  updateCharCount(): void {
    this.notesCharCount = this.notes.value ? this.notes.value.length : 0;
  }

  onReset() {
    this.submitAttempted = false;
    this.canShowFuelFields = false;
    this.canShowMaintFields = false;
    this.canShowLodgingFields = false;
    this.mileage.clearValidators();
    this.gallons.clearValidators();
    this.nights.clearValidators();
    this.resetControls([
      this.amount,
      this.selectedDate,
      this.employeeControl,
      this.jobControl,
      this.equipmentControl,
      this.expenseControl,
      this.mileage,
      this.gallons,
      this.nights,
      this.notes,
    ]);

    this.selectedDate.reset(new Date());
    this.removeImage(undefined);
    this.expenseControl.reset('--');
    let test = 1;
  }

  resetControls(controls: AbstractControl[]) {
    controls.forEach((control) => {
      control.reset();
      control.markAsPristine();
      control.markAsUntouched();
      control.setErrors(null);
    });
  }

  // Users who cannot pick an employee never see the Employee field, so their
  // receipts always belong to the logged-in user (Teamwork 44397382). The API
  // enforces the same rule.
  resolveEmployeeId(): string | null {
    if (!this.canModifyEmployee) {
      return this.viewingUserInfo?.id != null
        ? `${this.viewingUserInfo.id}`
        : null;
    }

    const selectedEmployee = this.users?.find(
      (el) => this.getUserLabel(el) === this.employeeControl.value
    );

    return selectedEmployee ? `${selectedEmployee.id}` : null;
  }

  // Labels of the values blocking a submit, in form order.
  getInvalidFields(): string[] {
    const invalid: string[] = [];

    if (!(this.images?.length > 0)) invalid.push('Receipt image');
    if (!this.resolveEmployeeId()) invalid.push('Employee');
    if (!this.amount.valid) invalid.push('Amount');
    if (!this.selectedDate.valid) invalid.push('Receipt Date');
    if (!this.expenseControl.valid) invalid.push('Expense Category');
    if (
      (this.canShowFuelFields || this.canShowMaintFields) &&
      !this.equipmentControl.valid
    ) {
      invalid.push('Unit');
    }
    if (this.canShowFuelFields && !this.mileage.valid) invalid.push('Mileage');
    if (this.canShowFuelFields && !this.gallons.valid) invalid.push('Gallons');
    if (this.canShowLodgingFields && !this.nights.valid) invalid.push('Nights');
    if (!this.notes.valid) invalid.push('Notes');

    return invalid;
  }

  async onSubmit() {
    try {
      this.isSubmitting = true;
      this.submitAttempted = true;
      this.loading$.next(true);
      // check for valid values; never block silently, since some required
      // values (like the employee) have no visible field for field crew roles
      const invalidFields = this.getInvalidFields();
      if (invalidFields.length) {
        this.alertMessageCommonService.error(
          'error',
          `Please check: ${invalidFields.join(', ')}.`
        );
      } else {
        const slug = this.mode === 'add' ? this.mode : this.id;

        const url = `${environment.apiUrl}/expenses/${slug}`;

        const urlObj = new URL(url);

        const employee_id = this.resolveEmployeeId();

        const existsInFavoriteJobs = this.favoriteJobs.find(
          (el) => this.getJobLabel(el) === this.jobControl.value
        );
        const existsInJobs = this.jobs.find(
          (el) => this.getJobLabel(el) === this.jobControl.value
        );

        const selectedExpense = this.expenseCategories.find(
          (el) => el.name === this.expenseControl.value
        );

        const selectedEquipment = this.equipments.find(
          (el) => this.getEquipmentLabel(el) === this.equipmentControl.value
        );

        const selectedJob = existsInFavoriteJobs ?? existsInJobs;

        const job_id: string = `${selectedJob?.id}`;

        const token = this.getToken();

        const form = new FormData();
        form.append('employee_id', employee_id);
        form.append('input_files', this.images[0], this.images[0].name);
        form.append('job_id', job_id);
        form.append('amount', this.amount.value.replace(/,/g, ''));
        form.append('receipt_date', this.selectedDate.value);
        form.append('receipt_type', this.selectedIndex == 0 ? '1' : '2');
        form.append('expense_category_id', `${selectedExpense?.id ?? ''}`);
        form.append('notes', this.notes.value);

        form.append('equipment_id', `${selectedEquipment?.id ?? ''}`);
        form.append('ce_equipment_id', `${selectedEquipment?.id ?? ''}`);

        if (this.mileage.value) {
          form.append('mileage', this.mileage.value.replace(/,/g, ''));
        }

        if (this.canShowFuelFields && this.gallons.value) {
          form.append('gallons', `${this.gallons.value}`.replace(/,/g, ''));
        }

        if (this.canShowLodgingFields && this.nights.value) {
          form.append('nights', `${this.nights.value}`.replace(/,/g, ''));
        }

        const httpHeaders = {
          Authorization: `Bearer ${token}`,
        };

        const headers = new Headers(httpHeaders);
        const method = this.mode === 'add' ? 'POST' : 'PUT';

        const options: RequestInit = {
          method,
          headers,
          body: form,
          redirect: 'follow',
        };

        const didSubmit = await this.expenseService.postNewExpense(
          urlObj.toString(),
          options
        );

        if (didSubmit) {
          this.isSubmitting = false;
          this.onReset();
          this.sweetAlertService.saveEntry();
          this.route.navigate(['/receipt-capture']);
        } else {
          this.alertMessageCommonService.error(
            'error',
            'The receipt could not be saved. Please try again.'
          );
        }
      }
    } catch (error) {
      console.error('SUBMIT ERROR:', error);
      this.alertMessageCommonService.error(
        'error',
        `The receipt could not be submitted: ${error?.message ?? error}`
      );
    } finally {
      this.isSubmitting = false;
      123;

      this.loading$.next(false);
    }
  }

  ngOnDestroy(): void {
    this.dropArea?.removeEventListener('dragenter', () => {});
    this.dropArea?.removeEventListener('dragleave', () => {});
    this.dropArea?.removeEventListener('dragover', () => {});
    this.dropArea?.removeEventListener('drop', () => {});
  }
}
